import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Label, Select, MenuItem, Button, Icon, Utils } from 'mw-style-react';
import FormUtils from '@control-front-end/utils/formUtils';
import { GET_FORM } from '@control-front-end/common/constants/forms';
import useIntl from 'useIntl';
import FieldValue from './components/FieldValue/FieldValue';
import SelectActorsTemplate from '../../../SelectActorsTemplate';
import mes from './intl';
import '../../ActorsFiltersPopup.scss';

const EXCLUDED_CLASSES = ['upload', 'multiselect'];

function TemplateFields({ formId, q, errors, onChange }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const [formView, setFormView] = useState({});
  const { form = {} } = formView;
  const emptyItem = { id: 'tmp', value: '', title: '---' };
  const [fields, setFields] = useState([emptyItem]);
  const [formItems, setFormItems] = useState([]);

  /**
   * Парсинг строки q на ключ-значение
   */
  const parseQ = () => {
    const makedQ = [];
    const splitAnd = q.split('&');
    splitAnd.forEach((keyValue) => {
      const splitedV = keyValue.split('=');
      const [id, value] = splitedV;
      const v = decodeURIComponent(value);
      makedQ.push({ id, value: v });
    });
    setFields(makedQ);
  };

  useEffect(() => {
    if (formId) {
      dispatch({
        type: GET_FORM.REQUEST,
        payload: { formId },
        callback: (f) => {
          setFormView(f);
        },
      });
    }
    if (q) parseQ();
    else setFields([emptyItem]);
  }, [formId]);

  useEffect(() => {
    if (form.sections) {
      const formFields = [];
      const noValueClass = FormUtils.getNoValueClass();
      const newFormItems = FormUtils.getFormItems(form);
      for (const item of newFormItems) {
        const { class: className } = item;
        if (
          noValueClass.includes(className) ||
          EXCLUDED_CLASSES.includes(className)
        )
          continue;
        formFields.push(item);
      }
      setFormItems(formFields);
    }
  }, [formView]);

  /**
   * Добавление поля формы
   */
  const handleAddItem = () => {
    const newFields = [...fields, emptyItem];
    setFields(newFields);
  };

  /**
   * Удаление поля формы
   */
  const handleDelItem = (index) => {
    // не удаляем последний эл-т, только очищаем значение
    if (fields.length === 1) {
      if (fields[0].id === 'tmp') return;
      setFields([emptyItem]);
      return;
    }
    const newFields = fields.slice();
    newFields.splice(index, 1);
    setFields(newFields);
  };

  /**
   * Редактирование ключа/значения поля
   */
  const handleChangeField = ({ id, value, addParam }) => {
    const newFields = fields.slice();
    const [key, index] = id.split('_');
    const addQ = addParam ? `.${addParam}` : '';
    let newValue = fields[index];
    if (key === 'id') {
      newValue = { id: value, value: '' };
    } else {
      newValue[key] = value;
    }
    newFields.splice(index, 1, newValue);
    setFields(newFields);
    const newQ = {};
    newFields.forEach((i) => {
      if (i.id !== 'tmp') {
        newQ[`${i.id}${addQ}`] = i.value;
      }
    });
    const strQ = Utils.serialize(newQ, true);
    onChange({ id: 'q', value: strQ });
  };

  const getFieldTitle = (id) => {
    const field = formItems.find((item) => id.indexOf(item.id) === 0);
    return field ? field.title : '';
  };
  const renderFields = () => {
    const availableFields = formItems.filter(
      (item) => !fields.find((field) => field.id === item.id)
    );
    return (
      <div styleName="filters__popup__content__col">
        {fields.map((field, index) => (
          <div styleName="filters__popup__content__row field" key={index}>
            <div styleName="filters__popup__content__item">
              <Select
                id={`id_${index}`}
                styleName="filters__popup__content__select openDown"
                type="autocomplete"
                size="medium"
                bordered={true}
                value={getFieldTitle(field.id)}
                onChange={handleChangeField}
              >
                <MenuItem value="tmp" label="---" />
                {availableFields.map((i) => (
                  <MenuItem key={i.id} value={i.id} label={i.title} />
                ))}
              </Select>
            </div>
            <Label styleName="filters__popup__content__equal" value="=" />
            <div styleName="filters__popup__content__item action">
              <FieldValue
                {...field}
                formId={formId}
                index={index}
                form={form}
                formItems={formItems}
                handleChangeField={handleChangeField}
              />
              <div onClick={() => handleDelItem(index)}>
                <Icon type="close" size="small" />
              </div>
            </div>
            {index === fields.length - 1 && availableFields.length ? (
              <Button
                styleName="filters__popup__content__btn"
                type="text"
                size="smallplus"
                fontWeight="normal"
                label={t(mes.actorTmplAddOption)}
                onClick={handleAddItem}
              />
            ) : null}
          </div>
        ))}
      </div>
    );
  };
  return (
    <div styleName="filters__popup__content__section">
      <div styleName="filters__popup__content__row tmpl">
        <div styleName="filters__popup__content__item tpl">
          <Label value={t(mes.actorTmpl)} />
          <SelectActorsTemplate
            key={formId}
            bordered={true}
            placeholder={t(mes.selectFromList)}
            value={{ value: formId }}
            error={!!errors.formId}
            helperText={t(mes[errors.formId])}
            onChange={({ value }) => {
              onChange({
                id: 'formId',
                value: value.id,
                formTitle: value.title,
                error: value.error,
              });
            }}
          />
        </div>
      </div>
      {formId ? (
        <>
          <div styleName="filters__popup__content__section head">
            <div styleName="filters__popup__content__item">
              <Label value={t(mes.actorTmplKey)} />
            </div>
            <div styleName="filters__popup__content__item">
              <Label value={t(mes.actorTmplValue)} />
            </div>
          </div>
          {fields.length ? renderFields() : null}
        </>
      ) : null}
    </div>
  );
}

TemplateFields.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  q: PropTypes.string,
  errors: PropTypes.object,
  onChange: PropTypes.func,
};

export default TemplateFields;
